// WebRTC メディア面（使い捨て・通話ごとに張って切る＝設計正本 §4.1 / 設計v2 §2.2）。
// VS のシグナリングは non-trickle（candidate 個別交換なし・完全 SDP 一括）＋ iceServers 空
// （Tailscale 前提 D7）。コーデックは VS 側で opus 固定。

import { signalWsUrlFrom } from './voice-config';
import type { SignalMessage } from './voice-types';

export interface MediaSessionHandlers {
  onMediaState: (state: string) => void;
  onConnected: () => void;
  onClosed: (reason?: string) => void;
  onError: (message: string) => void;
  /** 100ms 間隔の音声レベル（0..1）。mic=上り / spk=下り */
  onLevels: (mic: number, spk: number) => void;
}

const ICE_GATHER_TIMEOUT_MS = 3000;

export class MediaSession {
  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private micStream: MediaStream | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private audioCtx: AudioContext | null = null;
  private levelTimer: number | null = null;
  private closed = false;

  constructor(private handlers: MediaSessionHandlers) {}

  /** マイク取得 → PC 構築 → offer(non-trickle) → answer 適用。確立で onConnected */
  async start(signalUrl: string): Promise<void> {
    // 1) マイク（コールセンター型: AEC/NS/AGC 有効）
    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    // 2) シグナリング WS（割当 TTL 60s 内に張る）
    const ws = new WebSocket(signalWsUrlFrom(signalUrl));
    this.ws = ws;
    await new Promise<void>((resolve, reject) => {
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error('signal WS connect failed'));
    });

    // 3) PeerConnection（STUN/TURN なし＝tailnet/localhost 直結）
    const pc = new RTCPeerConnection({ iceServers: [] });
    this.pc = pc;
    for (const track of this.micStream.getAudioTracks()) pc.addTrack(track, this.micStream);

    // 下り（TTS）は同一 m-line で返る（VS webrtc-endpoint が sendrecv 昇格・R1 裏取り済み）
    pc.ontrack = (ev) => {
      const el = this.ensureAudioEl();
      el.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
      void el.play().catch(() => {
        // autoplay block 時はユーザー操作起点で再試行される（発信/受諾クリックが起点なので通常来ない）
        this.handlers.onError('音声の自動再生がブロックされました。画面をクリックしてください。');
      });
      this.attachSpeakerMeter(ev.streams[0] ?? new MediaStream([ev.track]));
    };
    pc.onconnectionstatechange = () => {
      if (!this.pc) return;
      if (this.pc.connectionState === 'connected') this.handlers.onConnected();
      if (this.pc.connectionState === 'failed') this.fail('WebRTC 接続に失敗しました');
    };

    // 4) シグナリング受信
    ws.onmessage = (e) => {
      let msg: SignalMessage;
      try {
        msg = JSON.parse(String(e.data)) as SignalMessage;
      } catch {
        return;
      }
      if (msg.type === 'answer' && msg.sdp) {
        void pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp }).catch(() => {
          this.fail('answer SDP の適用に失敗しました');
        });
      } else if (msg.type === 'media-state' && msg.state) {
        this.handlers.onMediaState(msg.state);
        if (msg.state === 'connected') this.handlers.onConnected();
      } else if (msg.type === 'error') {
        this.fail(`シグナリングエラー: ${msg.reason ?? 'unknown'}`);
      }
    };
    ws.onclose = () => {
      if (!this.closed) this.handlers.onClosed('signal-ws-closed');
    };

    // 5) offer（non-trickle: ICE 収集完了まで待つ。localhost/tailnet 直結なので短い）
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await this.waitIceGatheringComplete(pc);
    ws.send(JSON.stringify({ type: 'offer', sdp: pc.localDescription?.sdp ?? offer.sdp }));

    this.attachMicMeter();
  }

  /** マイク送出 on/off（再ネゴ不要） */
  setMicEnabled(enabled: boolean): void {
    for (const t of this.micStream?.getAudioTracks() ?? []) t.enabled = enabled;
  }

  /** 下り再生 on/off（ホールド用） */
  setSpeakerMuted(muted: boolean): void {
    if (this.audioEl) this.audioEl.muted = muted;
  }

  /** 切断（bye→close）。drain（最終 summary 取得）は呼び出し側（call-store）が先に行う */
  stop(sendBye = true): void {
    this.closed = true;
    if (sendBye && this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'bye' }));
      } catch {
        /* close 済みは無視 */
      }
    }
    this.teardown();
  }

  private fail(message: string): void {
    if (this.closed) return;
    this.handlers.onError(message);
    this.stop(false);
    this.handlers.onClosed('error');
  }

  private teardown(): void {
    if (this.levelTimer != null) {
      window.clearInterval(this.levelTimer);
      this.levelTimer = null;
    }
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
    try {
      this.pc?.close();
    } catch {
      /* ignore */
    }
    this.pc = null;
    for (const t of this.micStream?.getTracks() ?? []) t.stop();
    this.micStream = null;
    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl.remove();
      this.audioEl = null;
    }
    void this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
  }

  private ensureAudioEl(): HTMLAudioElement {
    if (!this.audioEl) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.style.display = 'none';
      document.body.appendChild(el);
      this.audioEl = el;
    }
    return this.audioEl;
  }

  private waitIceGatheringComplete(pc: RTCPeerConnection): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      const timer = window.setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') {
          window.clearTimeout(timer);
          resolve();
        }
      });
    });
  }

  // ---- 音声レベルメータ（QA の before≠after 実測にも使う・設計v2 §2.2） ----
  private micAnalyser: AnalyserNode | null = null;
  private spkAnalyser: AnalyserNode | null = null;

  private ctx(): AudioContext {
    if (!this.audioCtx) this.audioCtx = new AudioContext();
    return this.audioCtx;
  }

  private attachMicMeter(): void {
    if (!this.micStream) return;
    try {
      const ctx = this.ctx();
      const src = ctx.createMediaStreamSource(this.micStream);
      this.micAnalyser = ctx.createAnalyser();
      this.micAnalyser.fftSize = 256;
      src.connect(this.micAnalyser);
      this.startLevelTimer();
    } catch {
      /* メータは補助機能。失敗しても通話は続行 */
    }
  }

  private attachSpeakerMeter(stream: MediaStream): void {
    try {
      const ctx = this.ctx();
      void ctx.resume().catch(() => {});
      const src = ctx.createMediaStreamSource(stream);
      this.spkAnalyser = ctx.createAnalyser();
      this.spkAnalyser.fftSize = 256;
      // Chrome の既知挙動: WebRTC リモート stream は Web Audio グラフが destination まで
      // 繋がっていないと解析器へサンプルが流れない。可聴再生は <audio> 側が担うので、
      // ここは gain=0 で destination に接続してグラフを"回す"だけ（二重再生しない）。
      const sink = ctx.createGain();
      sink.gain.value = 0;
      src.connect(this.spkAnalyser);
      this.spkAnalyser.connect(sink);
      sink.connect(ctx.destination);
      this.startLevelTimer();
    } catch {
      /* 同上 */
    }
  }

  private startLevelTimer(): void {
    if (this.levelTimer != null) return;
    const buf = new Uint8Array(256);
    const rms = (an: AnalyserNode | null): number => {
      if (!an) return 0;
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < an.fftSize; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / an.fftSize);
    };
    this.levelTimer = window.setInterval(() => {
      this.handlers.onLevels(rms(this.micAnalyser), rms(this.spkAnalyser));
    }, 100);
  }
}
